import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Anchor, Button, Card, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useAuth } from '../context/AuthContext';
import { mapApiError } from '../utils/apiError';

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await login(form.email, form.password);
      navigate('/jobs', { replace: true });
    } catch (err) {
      setError(mapApiError(err, t, 'login.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card shadow="sm" padding="xl" radius="lg" withBorder>
      <Stack gap="sm">
        <Title order={2}>{t('login.title')}</Title>
        <Text c="dimmed">{t('login.subtitle')}</Text>
        <form onSubmit={onSubmit}>
          <Stack gap="md">
            <TextInput
              label={t('login.email')}
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="your.email@example.com"
              required
              radius="md"
              disabled={loading}
            />
            <PasswordInput
              label={t('login.password')}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              radius="md"
              disabled={loading}
            />
            {error && <Text c="red.6" fz="sm">{error}</Text>}
            <Button type="submit" radius="md" size="md" loading={loading}>
              {t('login.submit')}
            </Button>
          </Stack>
        </form>
        <Text c="dimmed" fz="sm">
          {t('login.needAccount')}{' '}
          <Anchor component={Link} to="/register" fw={600} c="plum.6">
            {t('login.register')}
          </Anchor>
        </Text>
      </Stack>
    </Card>
  );
};

export default LoginPage;
